import { useFormik } from 'formik';
import { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { exchangeFormServices } from 'services/exchange-return-form';
import {
  COLLECT_TYPE,
  EXCHANGE_REASON,
  ExchangeReturnFormData,
  PostFormResponse,
  REQUESTER_TYPE,
  REQUEST_TYPE,
  RETURN_TYPE,
} from 'types';
import { SystemLogoIcon } from 'static/icons';
import {
  Button,
  Heading,
  Input,
  Select,
  Text,
  UploadFileButton,
} from 'components';
import { formValidations } from 'helpers';
import {
  FontSize,
  FontWeight,
  collectTypes,
  exchangeReasons,
  requestTypes,
  returnTypes,
  routes,
} from 'commons';
import { Success } from './components';
import * as S from './styles';

export function ExchangeReturnForm() {
  const title = routes.exchangeReturnForm.name;

  const [isLoading, setIsLoading] = useState(false);
  const [postResponse, setPostResponse] = useState<
    PostFormResponse | undefined
  >(undefined);

  const validationSchena = Yup.object().shape({
    note: Yup.string().required('Informações adicionais'),
    exchangeReason: Yup.string().required('Motivo'),
    skus: Yup.string().required('SKU(s)'),
    consignmentCode: Yup.string().required('Número do(s) pedido(s)'),
    requestType: Yup.string().required('O que precisa solicitar'),
    requesterType: Yup.string().required('Solicitante'),
    requesterEmail: Yup.string()
      .email('O e-mail não é válido')
      .required('E-mail da Loja'),
    requesterName: Yup.string().required('Nome do Solicitante da Loja'),
    storeCode: Yup.string().required('Código da Loja'),
  });

  const exchangeFormData = useFormik({
    validateOnBlur: false,
    validateOnChange: false,
    validateOnMount: false,

    initialValues: {
      storeCode: '',
      requesterEmail: '',
      requesterName: '',
      requesterType: REQUESTER_TYPE.NONE,
      requestType: REQUEST_TYPE.NONE,
      collectType: COLLECT_TYPE.NONE,
      returnType: RETURN_TYPE.NONE,
      consignmentCode: '',
      skus: '',
      exchangeReason: EXCHANGE_REASON.NONE,
      note: '',
      file: undefined,
    },

    validationSchema: validationSchena,

    validate: (values) => {
      formValidations.validationSchema({
        schema: validationSchena,
        values,
      });
    },

    onSubmit: async (values) => {
      setIsLoading(true);

      const {
        storeCode,
        requesterEmail,
        requesterName,
        requesterType,
        requestType,
        collectType,
        returnType,
        consignmentCode,
        skus,
        exchangeReason,
        note,
        file,
      } = values;

      let imgToken = '';

      if (file && imgToken === '') {
        imgToken = await exchangeFormServices.postImageAndGetToken(file);
      }

      const isCancelOrRefund =
        requestType === REQUEST_TYPE.CANCEL_OR_REFUND_TO_CUSTOMER;
      const isCollectAtCustomer =
        requestType === REQUEST_TYPE.TO_COLLECT_AT_CUSTOMER;
      const isExchange = collectType === COLLECT_TYPE.EXCHANGE;

      function renderToaster(message: string) {
        toast.warn(message, {
          role: 'alert',
          toastId: 'alert',
        });
        setIsLoading(false);
      }

      if (isCancelOrRefund) {
        if (!returnType) {
          renderToaster('Campo Forma de cancelamento necessário');
          return;
        }
        if (!imgToken) {
          renderToaster('Uma nota fiscal deve ser anexada');
          return;
        }
      }

      if (isCollectAtCustomer) {
        if (!collectType) {
          renderToaster('Campo Finalidade da coleta necessário');
          return;
        }
        if (collectType === COLLECT_TYPE.RETURN) {
          if (!returnType) {
            renderToaster('Campo Forma de devolução necessário');
            return;
          }
        }
      }

      const form: ExchangeReturnFormData = {
        status: 'NEW',
        subject: 'New store sale ticket',
        description: 'New store sale ticket - MS Zendesk',
        requester: {
          email: requesterEmail,
          name: requesterName,
          phone: null,
        },
        uploads: isCancelOrRefund && imgToken ? [imgToken] : null,
        storeCode,
        requestType,
        collectType: isCancelOrRefund ? COLLECT_TYPE.RETURN : collectType,
        orderCodes: consignmentCode,
        requesterType,
        returnType: isExchange ? RETURN_TYPE.TOTAL : returnType,
        reason: exchangeReason,
        skus,
        note,
      };

      const response = await exchangeFormServices.postFormData(form);

      if (response) {
        setPostResponse(response);
      }

      setIsLoading(false);

      return;
    },
  });

  const { values, handleChange, handleSubmit, setFieldValue } =
    exchangeFormData;
  const {
    storeCode,
    requesterEmail,
    requesterName,
    requesterType,
    requestType,
    collectType,
    consignmentCode,
    skus,
    note,
  } = values;

  useEffect(() => {
    setFieldValue('collectType', '');
    setFieldValue('file', undefined);
  }, [requestType, setFieldValue]);

  if (postResponse) {
    return (
      <S.Wrapper>
        <S.Header>
          <S.LogoWrapper>
            <SystemLogoIcon />
          </S.LogoWrapper>
        </S.Header>

        <S.Content>
          <S.Title>
            <Heading as="h1" weight={FontWeight.bold}>
              {title}
            </Heading>
          </S.Title>
          <Success response={postResponse} />
        </S.Content>
      </S.Wrapper>
    );
  }

  return (
    <S.Wrapper>
      <S.Header>
        <S.LogoWrapper>
          <SystemLogoIcon />
        </S.LogoWrapper>
      </S.Header>

      <S.Content>
        <S.Title>
          <Heading as="h1" weight={FontWeight.bold}>
            {title}
          </Heading>
          <Text size={FontSize.font12}>
            Preencha o formulário abaixo para enviar a solicitação de troca ou
            devolução do seu cliente para a nossa equipe de atendimento.
            <br />
            <span>* Campos obrigatórios</span>
          </Text>
        </S.Title>

        <S.Form onSubmit={handleSubmit}>
          <S.FormSection>
            <S.Title>
              <Heading size={FontSize.font16} weight={FontWeight.bold}>
                Informações da loja
              </Heading>
            </S.Title>
            <S.FormSectionContent>
              <S.FormColumn>
                <S.InputWrapper>
                  <S.Label htmlFor="storeCode">
                    Código da Loja <span>*</span>
                  </S.Label>
                  <Input
                    name="storeCode"
                    value={storeCode}
                    onChange={handleChange}
                    placeholder="Código da Loja"
                  />
                </S.InputWrapper>

                <S.InputWrapper>
                  <S.Label htmlFor="requesterEmail">
                    E-mail da Loja <span>*</span>
                  </S.Label>
                  <Input
                    name="requesterEmail"
                    value={requesterEmail}
                    onChange={handleChange}
                    placeholder="E-mail da Loja"
                  />
                </S.InputWrapper>
              </S.FormColumn>

              <S.FormColumn>
                <S.InputWrapper>
                  <S.Label htmlFor="requesterName">
                    Nome do Solicitante da Loja <span>*</span>
                  </S.Label>
                  <Input
                    name="requesterName"
                    value={requesterName}
                    onChange={handleChange}
                    placeholder="Nome do Solicitante da Loja"
                  />
                </S.InputWrapper>
                <S.InputWrapper>
                  <S.Label>
                    Solicitante <span>*</span>
                  </S.Label>
                  <S.FormRow>
                    <S.RadioWrapper>
                      <input
                        type="radio"
                        name="requesterType"
                        value={REQUESTER_TYPE.STORE}
                        checked={requesterType === REQUESTER_TYPE.STORE}
                        onChange={() =>
                          setFieldValue('requesterType', REQUESTER_TYPE.STORE)
                        }
                      />
                      <Text>Loja</Text>
                    </S.RadioWrapper>
                    <S.RadioWrapper>
                      <input
                        type="radio"
                        name="requesterType"
                        value={REQUESTER_TYPE.CUSTOMER}
                        checked={requesterType === REQUESTER_TYPE.CUSTOMER}
                        onChange={() =>
                          setFieldValue(
                            'requesterType',
                            REQUESTER_TYPE.CUSTOMER
                          )
                        }
                      />
                      <Text>Cliente</Text>
                    </S.RadioWrapper>
                  </S.FormRow>
                </S.InputWrapper>
              </S.FormColumn>
            </S.FormSectionContent>
          </S.FormSection>

          <S.FormSection>
            <S.Title>
              <Heading size={FontSize.font16} weight={FontWeight.bold}>
                Detalhes da solicitação
              </Heading>
            </S.Title>
            <S.FormSectionContent>
              <S.FormColumn>
                <S.InputWrapper>
                  <S.Label htmlFor="requestType">
                    O que precisa solicitar? <span>*</span>
                  </S.Label>

                  <Select.Wrapper
                    options={requestTypes}
                    name="requestType"
                    onChange={handleChange}
                  >
                    <Select.Input placeholder="Escolha uma opção" />

                    <Select.StatusArrow />
                  </Select.Wrapper>
                </S.InputWrapper>

                {requestType === REQUEST_TYPE.TO_COLLECT_AT_CUSTOMER && (
                  <S.InputWrapper>
                    <S.Label htmlFor="collectType">
                      Finalidade de coleta? <span>*</span>
                    </S.Label>

                    <Select.Wrapper
                      options={collectTypes}
                      name="collectType"
                      onChange={handleChange}
                    >
                      <Select.Input placeholder="Escolha uma opção" />

                      <Select.StatusArrow />
                    </Select.Wrapper>
                  </S.InputWrapper>
                )}
              </S.FormColumn>
              {(collectType === COLLECT_TYPE.RETURN ||
                requestType === REQUEST_TYPE.CANCEL_OR_REFUND_TO_CUSTOMER) && (
                <S.FormColumn>
                  <S.InputWrapper>
                    <S.Label htmlFor="returnType">
                      {collectType === COLLECT_TYPE.RETURN
                        ? 'A devolução'
                        : 'O cancelamento'}{' '}
                      irá ocorrer de forma <span>*</span>
                    </S.Label>

                    <Select.Wrapper
                      options={returnTypes}
                      name="returnType"
                      onChange={handleChange}
                    >
                      <Select.Input placeholder="Escolha uma opção" />

                      <Select.StatusArrow />
                    </Select.Wrapper>
                  </S.InputWrapper>
                </S.FormColumn>
              )}
            </S.FormSectionContent>

            {requestType === REQUEST_TYPE.CANCEL_OR_REFUND_TO_CUSTOMER && (
              <S.UploadWrapper>
                <UploadFileButton
                  handleAddFile={(file: File) => setFieldValue('file', file)}
                  handleRemoveFile={() => setFieldValue('file', undefined)}
                  required
                />
              </S.UploadWrapper>
            )}
          </S.FormSection>

          <S.FormSection>
            <S.Title>
              <Heading size={FontSize.font16} weight={FontWeight.bold}>
                Pedidos
              </Heading>
            </S.Title>
            <S.FormSectionContent $direction="column">
              <S.FormRow $full>
                <S.InputWrapper>
                  <S.Label htmlFor="consignmentCode">
                    Número do(s) Pedido(s) <span>*</span>
                  </S.Label>
                  <Input
                    name="consignmentCode"
                    value={consignmentCode}
                    placeholder="Digite o número do pedido"
                    onChange={handleChange}
                  />
                </S.InputWrapper>
              </S.FormRow>

              <S.FormRow $full>
                <S.InputWrapper>
                  <S.Label htmlFor="skus">
                    SKU(s) <span>*</span>
                  </S.Label>
                  <Input
                    name="skus"
                    value={skus}
                    placeholder="Digite o(s) SKU(s) do(s) produto(s)"
                    onChange={handleChange}
                  />
                </S.InputWrapper>
              </S.FormRow>

              <S.FormRow $full>
                <S.InputWrapper>
                  <S.Label htmlFor="exchangeReason">
                    Motivo <span>*</span>
                  </S.Label>

                  <Select.Wrapper
                    options={exchangeReasons}
                    name="exchangeReason"
                    onChange={handleChange}
                  >
                    <Select.Input placeholder="Escolha uma opção" />

                    <Select.StatusArrow />
                  </Select.Wrapper>
                </S.InputWrapper>
              </S.FormRow>
            </S.FormSectionContent>

            <S.TextAreaWrapper>
              <S.Label htmlFor="note">
                Informações adicionais <span>*</span>
              </S.Label>
              <textarea
                name="note"
                value={note}
                placeholder="Digite informações adicionais"
                onChange={handleChange}
              />
            </S.TextAreaWrapper>
          </S.FormSection>

          <S.ButtonWrapper>
            <Button type="submit" isLoading={isLoading} disabled={isLoading}>
              Enviar
            </Button>
          </S.ButtonWrapper>
        </S.Form>
      </S.Content>
    </S.Wrapper>
  );
}
